import { Suspense, lazy, useEffect, useState } from 'react';
import {
  MAX_NAME_LENGTH,
  ROOM_CODE_LENGTH,
  isValidRoomCode,
  newRoomCode,
  type DailySummary,
  type GameId,
} from '@ofc/shared';
import { Fold, recentRoom } from '@ofc/ui';
import type { useAccount } from '../auth';
import { SignIn } from '../components/AccountPanel';

// The wardrobe is the biggest thing on this page and most visits never open it.
// Inside a fold most people never open, so it is fetched only when somebody does.
const GlobalStats = lazy(() => import('../components/GlobalStats').then((m) => ({ default: m.GlobalStats })));

const PlayersList = lazy(() => import('../components/Players').then((m) => ({ default: m.PlayersList })));
const RecentlyPlayed = lazy(() => import('../components/Players').then((m) => ({ default: m.RecentlyPlayed })));

const Profile = lazy(() => import('../components/Wardrobe').then((m) => ({ default: m.Wardrobe })));
import { DisplaySettings } from '../components/Display';
import { NameCard } from '../components/NameCard';
import { prefetchGame } from '../loaders';
import { GameList } from './GameList';

export interface GameCard {
  id: GameId;
  title: string;
  tagline: string;
  icon: string;
  glow: string;
  available: boolean;
}

export const GAMES: GameCard[] = [
  {
    id: 'movie',
    title: 'Movie Game',
    tagline: 'Name a film that shares a face with the last one',
    icon: '🎬',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'quiz',
    title: 'Quiz Game',
    tagline: 'Trivia, fastest finger first',
    icon: '🎯',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'quiplash',
    title: 'Quiplash',
    tagline: 'Answer the prompt, vote for the funniest',
    icon: '🎤',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'fibbage',
    title: 'Fibbage',
    tagline: 'Make up an answer, spot the real one',
    icon: '🤬',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'enough',
    title: 'Enough About You',
    tagline: 'How well does the room actually know you?',
    icon: '🧠',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'wavelengths',
    title: 'Wavelengths',
    tagline: 'One clue, and a hidden point on a slider',
    icon: '📡',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'obscurity',
    title: 'Obscurity',
    tagline: 'Name something nobody else would think of',
    icon: '🔎',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'superlatives',
    title: 'Superlatives',
    tagline: 'Who here is most likely to…',
    icon: '🏆',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'chameleon',
    title: 'Chameleon',
    tagline: 'Everyone knows the secret word but one',
    icon: '🦎',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'hottakes',
    title: 'Hot Takes',
    tagline: 'Rate it out of ten, then read the room',
    icon: '🌶️',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'higherlower',
    title: 'Higher or Lower',
    tagline: 'Which film came out first? Keep the run going',
    icon: '📅',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'castreveal',
    title: 'Cast Reveal',
    tagline: 'Name the film as its cast is revealed',
    icon: '🎭',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'posterzoom',
    title: 'Poster Zoom',
    tagline: 'Name the film from a zoomed-in poster',
    icon: '🔍',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'sixdegrees',
    title: 'Six Degrees',
    tagline: 'Race to link two films',
    icon: '🔗',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'connect4',
    title: 'Connect Four',
    tagline: 'Four in a row, for two',
    icon: '🔴',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'noughts',
    title: 'Noughts & Crosses',
    tagline: 'Classic, or the nine-board Ultimate',
    icon: '❌',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'battleships',
    title: 'Battleships',
    tagline: 'Hide your fleet, sink theirs',
    icon: '🚢',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'dots',
    title: 'Dots and Boxes',
    tagline: 'Draw lines, close boxes, go again',
    icon: '📦',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'wordduel',
    title: 'Word Duel',
    tagline: 'Five letters, six tries, fewest wins',
    icon: '🔤',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'anagrams',
    title: 'Anagram Race',
    tagline: 'Seven letters, ninety seconds',
    icon: '🔠',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'poker',
    title: 'Poker',
    tagline: "Texas Hold'em with pretend chips",
    icon: '🃏',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'liarsdice',
    title: "Liar's Dice",
    tagline: 'Bid on hidden dice, call out the liars',
    icon: '🎲',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'whosaid',
    title: 'Who Said It',
    tagline: 'Answer about yourself, guess who wrote what',
    icon: '🕵️',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'wouldyourather',
    title: 'Would You Rather',
    tagline: 'Pick a side, then call how the room split',
    icon: '🤔',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'cheat',
    title: 'Cheat',
    tagline: 'Play cards face down, lie, and call out the liars',
    icon: '🤫',
    glow: 'var(--red)',
    available: true,
  },
  {
    id: 'rankem',
    title: "Rank 'Em",
    tagline: 'Guess how the judge ranked five things',
    icon: '🏅',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'spotlight',
    title: 'Spotlight',
    tagline: 'How well do you know each other?',
    icon: '🔦',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'blindranking',
    title: 'Blind Ranking',
    tagline: 'Rank ten things without seeing what comes next',
    icon: '🙈',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'onestar',
    title: 'One-Star Reviews',
    tagline: 'Trash a classic film in one line',
    icon: '⭐',
    glow: 'var(--amber)',
    available: true,
  },
  {
    id: 'wagerquiz',
    title: 'Wager Quiz',
    tagline: 'Bet on the category before you see the question',
    icon: '🎲',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'fakinit',
    title: "Fakin' It",
    tagline: 'One of you got a different question',
    icon: '🕵️',
    glow: 'var(--pink)',
    available: true,
  },
  {
    id: 'undercover',
    title: 'Undercover',
    tagline: 'One of you has a different word and does not know it',
    icon: '🤫',
    glow: 'var(--cyan)',
    available: true,
  },
  {
    id: 'mole',
    title: 'The Mole',
    tagline: 'A team quiz with a traitor who knows the answers',
    icon: '🕳️',
    glow: 'var(--green)',
    available: true,
  },
  {
    id: 'boxart',
    title: 'Box Art Zoom',
    tagline: 'Name the video game from its zoomed-in box art',
    icon: '🎮',
    glow: 'var(--amber)',
    available: true,
  },
];

/** The same glows as hex, for drawing on a canvas, where CSS variables do not reach. */
const GLOW_HEX: Record<string, string> = {
  'var(--pink)': '#ff2d95',
  'var(--cyan)': '#22e0ff',
  'var(--amber)': '#ffc247',
  'var(--green)': '#7cff4f',
};

export function gameGlow(game: GameId): string {
  return GLOW_HEX[GAMES.find((g) => g.id === game)?.glow ?? ''] ?? '#ff2d95';
}

/** The display name of a game, for anywhere that only has its id. */
export function gameTitle(game: GameId): string {
  return GAMES.find((g) => g.id === game)?.title ?? 'the game';
}

/**
 * The front door. Pick a game to host, or type a code to join one — the code alone
 * is enough, because the room directory knows which game it belongs to.
 */
export function Landing({
  account,
  initialName,
  onEnter,
  onTv,
}: {
  account: ReturnType<typeof useAccount>;
  initialName: string;
  /** Into a game's room, or a game night's lounge. */
  onEnter(game: GameId | 'lounge', code: string, name: string): void;
  /** This screen becomes the big shared screen for a room or a game night. */
  onTv(code: string): void;
}) {
  const [name, setName] = useState(initialName);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [tvCode, setTvCode] = useState('');
  /** The room you were last in, once the server has confirmed it still exists. */
  const [rejoin, setRejoin] = useState<{ game: GameId; roomCode: string } | null>(null);

  // Offered only once the directory confirms the room is still there, so the
  // button never sends somebody into a room that has been forgotten.
  useEffect(() => {
    const recent = recentRoom();
    if (!recent) return;
    let cancelled = false;
    fetch(`/api/rooms/${recent.roomCode}`)
      .then(async (res) => {
        if (!res.ok || cancelled) return;
        const { game } = (await res.json()) as { game: GameId };
        // A recycled code now belonging to another game is not your room.
        if (game === recent.game && !cancelled) setRejoin(recent);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const signedIn = account.session;
  const streak = useDailyStreak(signedIn?.token ?? null);
  const playerName = (signedIn?.user.username ?? name).trim();

  const host = (game: GameId) => {
    if (!playerName) return setError('Pick a name first.');
    onEnter(game, newRoomCode(), playerName);
  };

  const join = async () => {
    if (!playerName) return setError('Pick a name first.');
    const clean = code.trim().toUpperCase();
    if (!isValidRoomCode(clean)) {
      return setError(`A room code is ${ROOM_CODE_LENGTH} characters.`);
    }

    setJoining(true);
    setError(null);
    try {
      // The directory tells us which game the code belongs to.
      const res = await fetch(`/api/rooms/${clean}`);
      if (!res.ok) {
        setError('No room with that code. Check it and try again.');
        return;
      }
      const { game } = (await res.json()) as { game: GameId | 'lounge' };
      onEnter(game, clean, playerName);
    } catch {
      setError('Could not reach the server. Are you online?');
    } finally {
      setJoining(false);
    }
  };

  return (
    <main className="screen">
      {/* One column the width of the screen: an auto-sized grid column grows to fit
          its widest line, which pushed the page sideways on narrow phones. */}
      <div
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          placeItems: 'center',
          padding: '12px 0',
        }}
      >
        <div className="col" style={{ width: '100%', maxWidth: 400 }}>
          <header className="center" style={{ marginBottom: 2 }}>
            <div className="sign muted small" style={{ letterSpacing: '0.3em' }}>
              Organised
            </div>
            <div className="logo neon neon-pink flicker" style={{ fontSize: 'clamp(30px, 8vw, 46px)' }}>
              Fun Centre
            </div>
          </header>

          {signedIn ? (
            <>
              <NameCard
                user={signedIn.user}
                open={showAccount}
                onToggle={() => setShowAccount((v) => !v)}
                streak={streak}
              />
              {/* Right under the card that opens it, not at the bottom of the page. */}
              {showAccount && (
                <Suspense fallback={<p className="small muted center">Opening your profile…</p>}>
                  <Profile account={account} />
                </Suspense>
              )}
            </>
          ) : (
            <section className="panel marquee col" style={{ padding: '18px 14px' }}>
              <label className="col" style={{ gap: 6 }}>
                <span className="sign small muted">Your name</span>
                <input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError(null);
                  }}
                  placeholder="Your name"
                  maxLength={MAX_NAME_LENGTH}
                  autoComplete="nickname"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="done"
                />
              </label>
            </section>
          )}

          <button
            className="panel row"
            onPointerEnter={() => prefetchGame('daily')}
            onClick={() => {
              prefetchGame('daily');
              location.hash = '#/daily';
            }}
            style={{
              gap: 12,
              textAlign: 'left',
              padding: 14,
              borderColor: 'var(--cyan)',
              background:
                'linear-gradient(120deg, color-mix(in srgb, var(--cyan) 12%, transparent), color-mix(in srgb, var(--pink) 8%, transparent))',
            }}
          >
            <span aria-hidden style={{ fontSize: 28, width: 36, flexShrink: 0, textAlign: 'center' }}>
              📅
            </span>
            <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
              <span className="sign" style={{ fontSize: 17 }}>
                Daily challenge
              </span>
              <span className="small muted">
                {signedIn
                  ? 'Nine puzzles, the same for everyone today. Keep your streak going.'
                  : 'Sign in to play the nine daily puzzles and build a streak.'}
              </span>
            </span>
            <span className="sign small" style={{ color: 'var(--cyan)' }}>
              {signedIn ? 'Play' : '🔒'}
            </span>
          </button>

          {rejoin && (
            <button
              className="panel row"
              onClick={() => {
                if (!playerName) return setError('Pick a name first.');
                onEnter(rejoin.game, rejoin.roomCode, playerName);
              }}
              style={{
                gap: 12,
                textAlign: 'left',
                padding: 14,
                borderColor: 'var(--green)',
                boxShadow: '0 0 18px color-mix(in srgb, var(--green) 25%, transparent)',
              }}
            >
              <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
                <span className="sign small muted">Pick up where you left off</span>
                <span className="sign" style={{ fontSize: 17 }}>
                  Back to {gameTitle(rejoin.game)}
                </span>
              </span>
              <span className="sign" style={{ letterSpacing: '0.2em', color: 'var(--green)' }}>
                {rejoin.roomCode}
              </span>
            </button>
          )}

          <section className="panel col">
            {/* A form, and the label owns only the input.
                The button used to sit inside the label, which is why joining
                never worked on a phone: a tap on anything inside a label is
                forwarded to the label's control, and iOS hands the activation to
                the text box instead of firing the button. On desktop the click
                still got through, so it looked fine everywhere we tested it.
                Submitting the form also gets Enter for free. */}
            <form
              className="col"
              style={{ gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault();
                void join();
              }}
            >
              <label className="sign small muted" htmlFor="room-code">
                Join a friend's game
              </label>
              <div className="row">
                <input
                  id="room-code"
                  className="grow"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase());
                    setError(null);
                  }}
                  placeholder="ABCD"
                  maxLength={ROOM_CODE_LENGTH}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="go"
                  style={{
                    fontFamily: 'Bungee, system-ui, sans-serif',
                    letterSpacing: '0.35em',
                    textAlign: 'center',
                  }}
                />
                <button type="submit" disabled={joining} style={{ flexShrink: 0 }}>
                  {joining ? '…' : 'Join'}
                </button>
              </div>
            </form>

            {error && (
              <p className="small" style={{ color: 'var(--red)', margin: 0 }} role="alert">
                {error}
              </p>
            )}
          </section>

          {/* Game night: one code for the whole evening, above any one game. */}
          <button
            className="panel row"
            onClick={() => {
              if (!playerName) return setError('Pick a name first.');
              onEnter('lounge', newRoomCode(), playerName);
            }}
            style={{
              gap: 12,
              textAlign: 'left',
              padding: 14,
              minHeight: 76,
              borderColor: 'var(--amber)',
              background:
                'linear-gradient(120deg, color-mix(in srgb, var(--amber) 14%, transparent), color-mix(in srgb, var(--pink) 10%, transparent))',
              boxShadow: '0 0 22px color-mix(in srgb, var(--amber) 30%, transparent)',
            }}
          >
            <span aria-hidden style={{ fontSize: 28, width: 36, flexShrink: 0, textAlign: 'center' }}>
              🌙
            </span>
            <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
              <span className="sign" style={{ fontSize: 17 }}>
                Game night
              </span>
              <span className="small muted">One code all evening. Play any games, keep a running table.</span>
            </span>
            <span className="sign small" style={{ color: 'var(--amber)' }}>
              Start
            </span>
          </button>

          <Fold
            id="landing:games"
            label="Or host a game"
            summary={`${GAMES.filter((g) => g.available).length} games`}
            defaultOpen
          >
            <GameList games={GAMES} onHost={host} />
          </Fold>

          {!signedIn ? <SignIn account={account} /> : null}

          <Fold id="landing:players" label="👥 Players" summary="Everyone, ranked by level">
            <Suspense fallback={<p className="small muted">Loading…</p>}>
              <PlayersList token={signedIn?.token ?? null} />
            </Suspense>
          </Fold>

          {signedIn && (
            <Fold id="landing:recent" label="🕑 Played with" summary="Who you have played lately">
              <Suspense fallback={<p className="small muted">Loading…</p>}>
                <RecentlyPlayed token={signedIn.token} />
              </Suspense>
            </Fold>
          )}

          <Fold id="landing:stats" label="🏆 Hall of fame" summary="Every game, all time">
            <section className="panel col">
              <Suspense fallback={<p className="small muted">Counting…</p>}>
                <GlobalStats />
              </Suspense>
            </section>
          </Fold>

          <Fold id="landing:tv" label="📺 Use this screen as the TV">
            <form
              className="panel col"
              onSubmit={(e) => {
                e.preventDefault();
                const clean = tvCode.trim().toUpperCase();
                if (!isValidRoomCode(clean)) return setError(`A code is ${ROOM_CODE_LENGTH} characters.`);
                onTv(clean);
              }}
            >
              <label className="sign small muted" htmlFor="tv-code">
                Show a room or game night on this screen
              </label>
              <p className="small muted" style={{ margin: 0 }}>
                For a laptop or a TV everyone can see. It shows the questions and the reveals, and
                everyone plays on their own phone.
              </p>
              <div className="row">
                <input
                  id="tv-code"
                  className="grow"
                  value={tvCode}
                  onChange={(e) => setTvCode(e.target.value.toUpperCase())}
                  placeholder="ABCD"
                  maxLength={ROOM_CODE_LENGTH}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="go"
                  style={{ fontFamily: 'Bungee, system-ui, sans-serif', letterSpacing: '0.35em', textAlign: 'center' }}
                />
                <button type="submit" style={{ flexShrink: 0 }}>
                  Show
                </button>
              </div>
            </form>
          </Fold>

          <Fold id="landing:display" label="Display" summary="Text size, motion, colours">
            <DisplaySettings />
          </Fold>

          <p className="small muted center" style={{ margin: 0 }}>
            No account needed. Sign in only if you want your stats kept.
          </p>

          {/* Required by the licences of the data both games are built on. */}
          <p className="small muted center" style={{ margin: 0, opacity: 0.7, lineHeight: 1.6 }}>
            Film data from{' '}
            <a href="https://www.themoviedb.org/" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
              TMDB
            </a>
            . Quiz questions from{' '}
            <a href="https://opentdb.com/" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
              Open Trivia DB
            </a>
            , CC BY-SA 4.0.
          </p>
        </div>
      </div>
    </main>
  );
}

/**
 * The daily streak for the name card. Fetched once per visit; a failure simply
 * leaves the chip off rather than showing anything wrong.
 */
function useDailyStreak(token: string | null): { days: number; daysLeft: number | null } | null {
  const [streak, setStreak] = useState<{ days: number; daysLeft: number | null } | null>(null);
  useEffect(() => {
    if (!token) return setStreak(null);
    let cancelled = false;
    fetch('/api/daily', { headers: { authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok || cancelled) return;
        const summary = (await res.json()) as DailySummary;
        setStreak({ days: summary.streak, daysLeft: summary.playedToday ? null : summary.daysLeft });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);
  return streak;
}
